import {
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Box,
  Divider,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  stepToActiveStep,
  useCurrentAppointmentRequiresRtg,
  useWizardContext,
} from "./AppointmentWizardContext";
import { fetchAppointmentNotes } from "../../services/api";
import AutocompleteSelect from "../AutocompleteSelect";
import { useWizardSteps } from "./hooks/useWizardSteps";
import { useSnackbarMessage } from "../Snackbar";
import * as api from "../../services/api";
import { status } from "shared/src/appointment.mjs";
import { FormErrorList, useFormAction } from "../../form";

export default function NotesForReceptionForm({ services, appointmentId }) {
  const { t } = useTranslation();
  const {
    receptionistNotes,
    updateReceptionistNotes,
    rtgNotRequired,
    setRtgNotRequired,
    appointmentNeeded,
    setAppointmentNeeded,
    setAppointmentNeededTouched,
    rtgCreated,
    activeStep,
    _setActiveStep,
  } = useWizardContext();
  const { getNextValidStep } = useWizardSteps();
  const displaySnackbarMessage = useSnackbarMessage();

  const [validationErrors, setValidationErrors] = useState({});

  const requiresRtgForThisAppointment = useCurrentAppointmentRequiresRtg();

  useEffect(() => {
    async function loadNotes() {
      if (!appointmentId) return;
      const response = await fetchAppointmentNotes(appointmentId);
      if (response?.appointmentNotes?.length > 0) {
        const note = response.appointmentNotes[0];
        const count = note.reservation_count ?? 0;

        const newNote = {
          service_id: note.service_id || "",
          reservation_count: count,
          reservation_unit:
            count === 0
              ? "earliestAvailableDate"
              : note.reservation_unit || "days",
          note_content: note.note_content || "",
          doctor_required: note.doctor_required || false,
        };

        updateReceptionistNotes(newNote);
        const appointmentValue = count === 0 ? "no" : "yes";
        setAppointmentNeeded(appointmentValue);

        if (appointmentValue) {
          setAppointmentNeededTouched(true);
        }
      }
    }
    loadNotes();
  }, [
    appointmentId,
    updateReceptionistNotes,
    setAppointmentNeeded,
    setAppointmentNeededTouched,
  ]);

  const handleNotesChange = (field) => (e) => {
    const value = e.target.value;

    if (field === "reservation_count") {
      const count =
        value === ""
          ? null
          : Number.isFinite(Number(value))
            ? Number(value)
            : null;

      updateReceptionistNotes({ reservation_count: count });
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next.reservation_count;
        return next;
      });
      return;
    }

    if (field === "reservation_unit") {
      const unit = value;
      const prev = receptionistNotes.reservation_count;
      const count = unit === "earliestAvailableDate" ? 0 : (prev ?? null);

      updateReceptionistNotes({
        reservation_unit: unit,
        reservation_count: count,
      });
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next.reservation_count;
        return next;
      });
      return;
    }

    if (field === "doctor_required") {
      updateReceptionistNotes({ doctor_required: e.target.checked });
      return;
    }

    updateReceptionistNotes({ [field]: value });
  };

  const handleAppointmentNeededChange = (e) => {
    const val = e.target.value;
    setAppointmentNeeded(val);
    setAppointmentNeededTouched(true);

    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.appointmentNeeded;
      if (val === "no") {
        delete next.reservation_count;
      }
      return next;
    });

    if (val === "no") {
      updateReceptionistNotes({
        reservation_unit: "earliestAvailableDate",
        reservation_count: 0,
      });
    } else if (val === "yes") {
      updateReceptionistNotes({
        reservation_unit: "weeks",
        reservation_count: null,
      });
    }
  };

  const unitSelectValue =
    appointmentNeeded === "yes"
      ? (receptionistNotes?.reservation_unit ?? "weeks")
      : receptionistNotes?.reservation_unit || "earliestAvailableDate";

  const numberFieldValue =
    unitSelectValue === "earliestAvailableDate"
      ? ""
      : (receptionistNotes?.reservation_count ?? "");

  const buildReceptionistNotePayload = (
    appointmentId,
    notes,
    needNewAppointment,
  ) => {
    const base = {
      appointment_id: appointmentId,
      note_type: "receptionist",
      note_content: notes?.note_content ?? "",
      doctor_required: Boolean(notes?.doctor_required),
    };

    if (needNewAppointment === "no") {
      return {
        ...base,
        service_id: null,
        reservation_unit: null,
        reservation_count: null,
      };
    }

    const unit = notes?.reservation_unit ?? null;
    const count =
      unit === "earliestAvailableDate"
        ? 0
        : Number.isFinite(Number(notes?.reservation_count))
          ? Number(notes?.reservation_count)
          : null;

    return {
      ...base,
      service_id: notes?.service_id || null,
      reservation_unit: unit,
      reservation_count: count,
    };
  };

  const { errors, onSubmit } = useFormAction(
    async () => {
      const nextStep = getNextValidStep(activeStep);

      return api.patchAppointment({
        id: appointmentId,
        status_id: status.TREATED,
        active_step: stepToActiveStep(nextStep),
        notes: [
          buildReceptionistNotePayload(
            appointmentId,
            receptionistNotes,
            appointmentNeeded,
          ),
        ],
      });
    },
    () => {
      const nextStep = getNextValidStep(activeStep);
      _setActiveStep(nextStep);
      displaySnackbarMessage({
        title: t("Zápis pro recepci byl úspěšně odeslán."),
      });
    },
  );

  return (
    <>
      <Typography variant="h2" sx={{ mb: 2 }}>
        {t("appointmentNotes.notesForReception")}
      </Typography>
      <form id="appointmentWizard" onSubmit={onSubmit}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid>
            <FormControl
              sx={{ minWidth: 240 }}
              required={appointmentNeeded === "yes"}
              error={!!validationErrors.service_id}
            >
              <AutocompleteSelect
                name="service"
                label={t("appointmentNotes.plannedService")}
                options={services}
                value={services.find(
                  (s) => s.id === receptionistNotes?.service_id,
                )}
                required={appointmentNeeded === "yes"}
                onChange={(value) => {
                  const service_id = value?.id;

                  updateReceptionistNotes({ service_id });
                  setValidationErrors((prev) => {
                    const next = { ...prev };
                    delete next.service_id;
                    return next;
                  });
                }}
              />
            </FormControl>
          </Grid>

          <Grid>
            <FormControlLabel
              control={
                <Checkbox
                  name="doctor_required"
                  onChange={handleNotesChange("doctor_required")}
                  checked={Boolean(receptionistNotes?.doctor_required)}
                />
              }
              label={t("appointmentNotes.doctorRequired")}
            />
          </Grid>

          <Grid>
            <TextField
              name="note_content"
              id="note_content"
              label={t("appointmentNotes.quickInfo")}
              onChange={handleNotesChange("note_content")}
              value={receptionistNotes?.note_content || ""}
              multiline
              fullWidth
              rows={2}
            />
          </Grid>
        </Grid>

        {requiresRtgForThisAppointment && !rtgCreated && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("appointmentNotes.rtgTeleSection", "RTG/TELE")}
              </Typography>

              <Typography variant="body2" sx={{ mb: 2 }}>
                Rentgen je u této schůzky povinný. Vytvořte ad hoc RTG nebo
                potvrďte, že RTG/TELE neproběhne.
              </Typography>

              <FormControlLabel
                required
                control={
                  <Checkbox
                    checked={rtgNotRequired}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setRtgNotRequired(next);
                    }}
                    color="primary"
                  />
                }
                label={t(
                  "appointmentNotes.rtgNotRequired",
                  "RTG/TELE neproběhne",
                )}
              />
            </Box>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        <FormControl
          component="fieldset"
          sx={{ mb: 2 }}
          error={!!validationErrors.appointmentNeeded}
          required
        >
          <FormLabel component="legend">
            {t("appointmentNotes.appointmentNeeded")}
          </FormLabel>
          <RadioGroup
            row
            value={appointmentNeeded}
            onChange={handleAppointmentNeededChange}
          >
            <FormControlLabel
              value="yes"
              control={<Radio />}
              label={t("appointmentNotes.needNewAppointment")}
              required
            />
            <FormControlLabel
              value="no"
              control={<Radio />}
              label={t("appointmentNotes.noNewAppointment")}
              required
            />
          </RadioGroup>
        </FormControl>

        {appointmentNeeded === "yes" && (
          <Grid container spacing={2} alignItems="center">
            <Grid>
              <TextField
                sx={{ width: 120 }}
                name="reservation_count"
                id="reservation_count"
                label={t("appointmentNotes.number")}
                type="number"
                onChange={handleNotesChange("reservation_count")}
                value={numberFieldValue}
                error={!!validationErrors.reservation_count}
                slotProps={{ htmlInput: { min: 1 } }}
                required={unitSelectValue !== "earliestAvailableDate"}
              />
            </Grid>

            <Grid>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{t("appointmentNotes.unit")}</InputLabel>
                <Select
                  name="reservation_unit"
                  value={unitSelectValue}
                  onChange={handleNotesChange("reservation_unit")}
                >
                  <MenuItem value="earliestAvailableDate">
                    {t("appointmentNotes.earliestAvailableDate")}
                  </MenuItem>
                  <MenuItem value="days">{t("appointmentNotes.days")}</MenuItem>
                  <MenuItem value="weeks">
                    {t("appointmentNotes.weeks")}
                  </MenuItem>
                  <MenuItem value="months">
                    {t("appointmentNotes.months")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
        <FormErrorList formErrors={errors} />
      </form>
    </>
  );
}
